import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Activity, TrendingUp, AlertCircle, Filter, CheckCircle, XCircle } from 'lucide-react';
import { reportService } from '@/services/reportService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { UserLevel } from '@/types/auth.types';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface ProcedureType {
  procedure_type: string;
  count: number;
  success_count: number;
  success_rate: number;
  complications: number;
  reinterventions: number;
  avg_age_at_procedure_days: number;
  common_diagnoses?: string; // Comma-separated ICD-10 codes
}

interface ApiResponse {
  summary: {
    total_procedures: number;
    success_rate: number;        // Percentage (0-100)
    complication_rate: number;   // Percentage (0-100)
    reintervention_rate: number; // Percentage (0-100)
  };
  by_procedure_type: ProcedureType[];
  by_surgeon?: Array<{
    surgeon_name: string;
    procedures: number;
    success_rate: number;        // Percentage (0-100)
    avg_procedure_time_min: number; // Always 0 (not tracked in database)
  }>;
  complications_breakdown: Record<string, number>; // Romanian category names as keys
  error?: string; // Only present if error occurred
}

interface ProcedureTypeOption {
  id: number;
  name_ro: string;
  code: string;
}

interface HospitalOption {
  hospital_id: number;
  hospital_name: string;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export const ProcedureOutcomesReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hospitalId, setHospitalId] = useState('all');
  const [procedureTypeId, setProcedureTypeId] = useState('all');

  const [procedureTypes, setProcedureTypes] = useState<ProcedureTypeOption[]>([]);
  const [hospitals, setHospitals] = useState<HospitalOption[]>([]);

  const { toast } = useToast();
  const { utilizator, nivelUtilizator } = useAuth();

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        // Fetch procedure types
        const typesResponse = await api.get<ProcedureTypeOption[]>('/api/procedures/types');
        if (typesResponse.success && typesResponse.data) {
          setProcedureTypes(typesResponse.data);
        }

        // Fetch hospitals if admin
        if (nivelUtilizator === UserLevel.ADMINISTRATOR) {
          const hospitalsResponse = await api.get<HospitalOption[]>('/api/reference/hospitals');
          if (hospitalsResponse.success && hospitalsResponse.data) {
            setHospitals(hospitalsResponse.data);
          }
        }
      } catch (error) {
        console.error('Error fetching reference data:', error);
      }
    };

    fetchReferenceData();
  }, [nivelUtilizator]);

  // Set initial hospital based on user role
  useEffect(() => {
    if (utilizator && nivelUtilizator === UserLevel.HOSPITAL && utilizator.hospital_id) {
      setHospitalId(utilizator.hospital_id.toString());
    }
  }, [utilizator, nivelUtilizator]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      // Handle hospital filter
      if (nivelUtilizator === UserLevel.HOSPITAL) {
        if (utilizator?.hospital_id) {
          filters.hospital_id = utilizator.hospital_id;
        }
      } else if (hospitalId && hospitalId !== 'all') {
        filters.hospital_id = parseInt(hospitalId);
      }

      // Handle procedure type filter
      if (procedureTypeId && procedureTypeId !== 'all') {
        filters.procedure_type_id = parseInt(procedureTypeId);
      }

      const response = await reportService.getProcedureOutcomesReport(filters);
      setData(response as unknown as ApiResponse);

    } catch (error) {
      console.error('Error fetching procedure outcomes:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele raportului de proceduri',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch when we have necessary initial data (e.g. hospital ID for hospital users)
    if (nivelUtilizator === UserLevel.HOSPITAL && !utilizator?.hospital_id) return;

    fetchData();
  }, [utilizator]); // Initial load

  const formatRate = (rate: number | undefined | null) => {
    if (typeof rate === 'number') {
      return rate.toFixed(1);
    }
    return '0.0';
  };

  const complicationsData = data
    ? Object.entries(data.complications_breakdown).map(([type, count]) => ({
      name: type,
      value: count,
    }))
    : [];

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  const isAdmin = nivelUtilizator === UserLevel.ADMINISTRATOR;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtre raport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="start-date">Data început</Label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-date">Data sfârșit</Label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="hospital">Spital</Label>
              <Select
                value={hospitalId}
                onValueChange={setHospitalId}
                disabled={!isAdmin}
              >
                <SelectTrigger id="hospital">
                  <SelectValue placeholder={isAdmin ? "Toate spitalele" : (utilizator?.hospital_name || "Spitalul meu")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate spitalele</SelectItem>
                  {hospitals.map((hospital) => (
                    <SelectItem key={hospital.hospital_id} value={hospital.hospital_id.toString()}>
                      {hospital.hospital_name}
                    </SelectItem>
                  ))}
                  {!isAdmin && utilizator?.hospital_id && (
                    <SelectItem value={utilizator.hospital_id.toString()}>
                      {utilizator.hospital_name || "Spitalul curent"}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="procedure">Tip procedură</Label>
              <Select value={procedureTypeId} onValueChange={setProcedureTypeId}>
                <SelectTrigger id="procedure">
                  <SelectValue placeholder="Toate procedurile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate procedurile</SelectItem>
                  {procedureTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name_ro}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchData} className="w-full">
                <Filter className="h-4 w-4 mr-2" />
                Aplică filtre
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Activity className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="text-3xl font-bold text-blue-600">{data.summary.total_procedures}</p>
                  <p className="text-sm text-muted-foreground">Total proceduri</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="text-3xl font-bold text-green-600">{formatRate(data.summary.success_rate)}%</p>
                  <p className="text-sm text-muted-foreground">Rata succes</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                  <p className="text-3xl font-bold text-orange-600">{formatRate(data.summary.complication_rate)}%</p>
                  <p className="text-sm text-muted-foreground">Rata complicații</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <XCircle className="h-8 w-8 mx-auto mb-2 text-red-600" />
                  <p className="text-3xl font-bold text-red-600">{formatRate(data.summary.reintervention_rate)}%</p>
                  <p className="text-sm text-muted-foreground">Rata reintervenții</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Rezultate pe tip de procedură</CardTitle>
            </CardHeader>
            <CardContent>
              {data.by_procedure_type.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tip procedură</TableHead>
                        <TableHead className="text-center">Total</TableHead>
                        <TableHead className="text-center">Succes</TableHead>
                        <TableHead className="text-center">Rata succes</TableHead>
                        <TableHead className="text-center">Complicații</TableHead>
                        <TableHead className="text-center">Reintervenții</TableHead>
                        <TableHead className="text-center">Vârstă medie (zile)</TableHead>
                        <TableHead className="text-left">Diagnostice frecvente</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.by_procedure_type.map((procedure, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{procedure.procedure_type}</TableCell>
                          <TableCell className="text-center">{procedure.count}</TableCell>
                          <TableCell className="text-center">{procedure.success_count}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-semibold ${getSuccessRateColor(procedure.success_rate)}`}>
                              {formatRate(procedure.success_rate)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {procedure.complications > 0 ? (
                              <Badge variant="destructive">{procedure.complications}</Badge>
                            ) : (
                              <Badge variant="outline">0</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {procedure.reinterventions > 0 ? (
                              <Badge variant="secondary">{procedure.reinterventions}</Badge>
                            ) : (
                              <Badge variant="outline">0</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {Math.round(procedure.avg_age_at_procedure_days)}
                          </TableCell>
                          <TableCell className="text-left text-sm text-gray-600">
                            {procedure.common_diagnoses || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nu există date disponibile pentru proceduri în perioada selectată
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {data.by_surgeon && data.by_surgeon.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Performanță pe operator</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Operator</TableHead>
                        <TableHead className="text-center">Proceduri</TableHead>
                        <TableHead className="text-center">Rata succes</TableHead>
                        <TableHead className="text-center">Timp mediu (min)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.by_surgeon.map((surgeon, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{surgeon.surgeon_name}</TableCell>
                          <TableCell className="text-center">{surgeon.procedures}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-semibold ${getSuccessRateColor(surgeon.success_rate)}`}>
                              {formatRate(surgeon.success_rate)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">{surgeon.avg_procedure_time_min}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Distribuție complicații</CardTitle>
            </CardHeader>
            <CardContent>
              {complicationsData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={complicationsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {complicationsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2">
                    <h4 className="font-semibold mb-4">Detalii complicații</h4>
                    {complicationsData.map((complication, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="font-medium">{complication.name}</span>
                        </div>
                        <Badge>{complication.value} cazuri</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nu există complicații înregistrate în perioada selectată
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
